// src/services/kioskAuthService.ts
import { scrypt } from 'scrypt-js';

const ALGORITHM = 'AES-GCM';

const API_BASE = process.env.REACT_APP_IMS_API_BASE_URL || 'http://localhost:4001';
const KIOSK_TOKEN_PATH =
  process.env.REACT_APP_KIOSK_TOKEN_PATH || '/api/auth/generate-kiosk-token';

const POS_APP_ID = process.env.REACT_APP_POS_APP_ID || '';
const POS_APP_KEY = process.env.REACT_APP_POS_APP_KEY || '';

// cache (same idea as before)
let cachedToken: string | null = null;
let cachedExpiresAt: number | null = null;
let inflight: Promise<string> | null = null;

type TokenApiResponse = {
  token: string;
  expiresAt?: number;   // ms
  expiresIn?: number;   // seconds
};

function nowMs() {
  return Date.now();
}

function makeAccessIdentifier() {
  // unique per request
  const rand = new Uint8Array(8);
  crypto.getRandomValues(rand);
  return `${nowMs()}-${bytesToHex(rand)}`;
}

function utf8ToBytes(s: string) {
  return new TextEncoder().encode(s);
}

function bytesToHex(bytes: Uint8Array) {
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

// MUST match backend: crypto.scryptSync(access_identifier, 'salt', 32)
// Node defaults: N=16384, r=8, p=1, dkLen=32
async function deriveKeyBytesFromAccessIdentifier(accessIdentifier: string): Promise<Uint8Array> {
  const password = utf8ToBytes(accessIdentifier);
  const salt = utf8ToBytes('salt');
  const N = 16384;
  const r = 8;
  const p = 1;
  const dkLen = 32;

  const derived = await scrypt(password, salt, N, r, p, dkLen);
  return new Uint8Array(derived);
}

// Encrypt POS_APP_KEY with access_identifier using AES-256-GCM
// Output format MUST match backend decryptSecret(): ivHex.encryptedHex.authTagHex
async function encryptAppKey(appKey: string, accessIdentifier: string): Promise<string> {
  const keyBytesRaw = await deriveKeyBytesFromAccessIdentifier(accessIdentifier);

  // force a real ArrayBuffer-backed Uint8Array (fixes TS BufferSource issue)
  const keyBytes = new Uint8Array(keyBytesRaw);


  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: ALGORITHM },
    false,
    ['encrypt']
  );

  // backend uses 16-byte IV (randomBytes(16)) — WebCrypto supports that
  const iv = new Uint8Array(16);
  crypto.getRandomValues(iv);

  const plaintext = utf8ToBytes(appKey);

  // WebCrypto returns ciphertext + authTag appended at end (tagLength=128 bits => 16 bytes)
  const encryptedBuf = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv, tagLength: 128 },
    cryptoKey,
    plaintext
  );

  const encryptedAll = new Uint8Array(encryptedBuf);
  const tagLen = 16;

  const cipherText = encryptedAll.slice(0, encryptedAll.length - tagLen);
  const authTag = encryptedAll.slice(encryptedAll.length - tagLen);

  return `${bytesToHex(iv)}.${bytesToHex(cipherText)}.${bytesToHex(authTag)}`;
}

export function clearKioskToken() {
  cachedToken = null;
  cachedExpiresAt = null;
  inflight = null;
}

export async function getKioskToken(force = false): Promise<string> {
  if (!POS_APP_ID || !POS_APP_KEY) {
    throw new Error('Missing REACT_APP_POS_APP_ID / REACT_APP_POS_APP_KEY');
  }

  const stillValid =
    !force &&
    cachedToken &&
    cachedExpiresAt &&
    cachedExpiresAt - nowMs() > 30_000;

  if (stillValid) return cachedToken!;
  if (inflight) return inflight;

  inflight = (async () => {
    const accessIdentifier = makeAccessIdentifier();
    const encryptedSecret = await encryptAppKey(POS_APP_KEY, accessIdentifier);

    const res = await fetch(`${API_BASE}${KIOSK_TOKEN_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        app_id: POS_APP_ID,
        access_identifier: accessIdentifier,
        access_secret: encryptedSecret,
      }),
    });

    const data = await res.json().catch(() => null);

    if (!res.ok) {
      inflight = null;
      const msg = data?.message || data?.error || `HTTP ${res.status}`;
      throw new Error(msg);
    }

    const tokenData = data as TokenApiResponse;

    cachedToken = tokenData.token;
    cachedExpiresAt =
      typeof tokenData.expiresAt === 'number'
        ? tokenData.expiresAt
        : (typeof tokenData.expiresIn === 'number'
          ? nowMs() + tokenData.expiresIn * 1000
          : nowMs() + 24 * 60 * 60 * 1000); // fallback 24h

    inflight = null;
    return cachedToken!;
  })();

  return inflight;
}
